/*
 * EventLogo — circular event identity element.
 *
 * If the event has a logo (data URL or external URL), render it inside
 * a rounded mask. Otherwise fall back to a monogram of the first letter
 * of the event name on a muted background. Same shape as TeamAvatar and
 * PlayerAvatar so the three identity elements stack cleanly when they
 * appear side by side.
 *
 * Use throughout the app wherever an event is identified: tournaments
 * list cards, event detail hero, join-via-code preview, etc.
 */

interface Props {
  logoUrl?: string | null;
  name?: string | null;
  size?: number;
  /** Faint white ring so the logo reads on tinted backgrounds. */
  ring?: boolean;
}

export function EventLogo({ logoUrl, name, size = 40, ring = false }: Props) {
  const radius = size / 2;
  const ringStyle = ring ? { boxShadow: '0 0 0 1px rgba(255,255,255,0.2)' } : {};

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name ?? 'Event logo'}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          objectFit: 'cover',
          flexShrink: 0,
          background: '#0a0a0a',
          ...ringStyle,
        }}
      />
    );
  }

  const initial = (name ?? '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: radius,
        background: 'var(--input)',
        border: '1px solid var(--border)',
        color: 'var(--text-sec)',
        fontSize: Math.max(12, Math.round(size * 0.42)),
        fontWeight: 800,
        lineHeight: 1,
        flexShrink: 0,
        userSelect: 'none',
        ...ringStyle,
      }}
    >
      {initial}
    </span>
  );
}
